// GridView.js – with conditional drag-drop, col-4 for all types, and custom case card layout
import React from 'react';
import { Card, CardBody, Badge, Row, Col } from 'reactstrap';
import { Link, NavLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsisV, faUserCircle } from '@fortawesome/free-solid-svg-icons';

const getBadgeColor = (status) => {
    switch (status) {
        case 'Active': return 'success';
        case 'Inactive': return 'warning';
        case 'Pending': return 'danger';
        case 'Reject': return 'error';
        case 'Completed': return 'primary';
        default: return 'info';
    }
};
const getWinColor = (percentage) => {
    if (percentage >= 70) return '#2ecc71'; // green
    if (percentage >= 40) return '#f39c12'; // orange
    return '#e74c3c'; // red
};
const stringToHue = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return Math.abs(hash % 360);
};
const getInitials = (firstName, lastName) => {
    if (firstName && lastName) {
        return `${firstName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
        return firstName.substring(0, 1).toUpperCase();
    }
    return '?';
};
const getRoleBadgeColor = (role) => {
    switch (role) {
        case 'Admin': return 'success';
        case 'User': return 'info';
        case 'Manager': return 'warning';
        default: return 'secondary';
    }
};

const GridView = ({
    data,
    type,
    sidePanelDetails,
    tablefieldsToShow,
    openViewModal,
    openFormModal,
    showOverlay,
    sendRecentViewedData,
    onDragStart,
    onDragOver,
    onDragEnter,
    onDragLeave,
    onDrop,
    dateFormats,
    config,
    flattenArray,
    getBadgeData,
    getMultiSelectBadge,
    customChangeFieldValues,
    isItemActive
}) => {
    if (!data || data.length === 0) return null;

    const fileFolderTypes = ['SharedFiles', 'Files', 'Folders'];
    const isFileFolder = fileFolderTypes.includes(type);

    const getCardClassName = (item) => {
        const isActive = isItemActive
            ? isItemActive(item)
            : type === 'SharedFiles'
                ? sidePanelDetails?.fileId === item?.fileId?._id
                : sidePanelDetails?.fileId === item?._id;

        const baseClass = !isFileFolder ? 'folder-box-user' : 'folder-box mx-2';
        return isActive ? `active_summary_file ${baseClass}` : baseClass;
    };

    const renderField = (fieldConfig, item) => {
        const { field, fieldType, showInGrid, displayInSettings, customWidth, designIcon, style, isMultiSelect } = fieldConfig;

        if (showInGrid === false || displayInSettings === false) return null;

        const value = item[field];
        const fieldValue = type === 'SharedFiles'
            ? item?.type ? item?.fileId?.name : item?.folderId?.name
            : value;

        const widthClass = customWidth || 'col-12';

        return (
            <div key={field} className={widthClass} title={field === 'name' ? fieldValue : ''}>
                <div className="overflow-x-hidden d-flex align-items-center">
                    {designIcon && value && (
                        <FontAwesomeIcon
                            icon={designIcon}
                            className="genderIconAlignment align_move mr-3px default-cursor"
                            style={{ color: '#999999' }}
                        />
                    )}

                    {fieldType === 'Link' && (
                        <div className="d-flex">
                            <span style={style} onClick={() => openViewModal(item, 'view')}>
                                <b>{console.log(item)}:{fieldValue}</b>
                            </span>
                        </div>
                    )}

                    {fieldType === 'Badge' && (
                        <div style={style}>
                            <Badge color={getBadgeColor(item.status)}>{fieldValue}</Badge>
                        </div>
                    )}

                    {fieldType === 'Role' && (
                        <div style={style}>
                            <Badge pill color="success">{fieldValue}</Badge>
                        </div>
                    )}

                    {fieldType === 'Date' && (
                        <div
                            className="subsubFields"
                            title={value ? dateFormats.formatDate(value, config.dateDayMonthFormat) : null}
                        >
                            {value ? dateFormats.formatDate(value, config.dateDayMonthFormat) : null}
                        </div>
                    )}

                    {fieldType === 'Time' && (
                        <div>
                            {dateFormats.formatDate(value, config.timeFormat)} - $
                            {dateFormats.formatDate(item.toTime, config.timeFormat)}
                        </div>
                    )}

                    {fieldType === 'Array' && (
                        <span style={style}>{flattenArray(value)}</span>
                    )}

                    {fieldType === 'dropDown' && !isMultiSelect && (
                        <div style={style}>{getBadgeData(fieldConfig, value)}</div>
                    )}

                    {fieldType === 'dropDown' && isMultiSelect && (
                        <div style={style}>{getMultiSelectBadge(fieldConfig, value)}</div>
                    )}

                    {fieldType === 'relateAutoComplete' && (
                        <span style={style}>
                            {value && fieldConfig.searchField && value[fieldConfig.searchField]}
                        </span>
                    )}

                    {fieldType === 'customType' && (
                        <div
                            className="d-flex flex-wrap gap-1"
                            style={{ fontWeight: 'bolder', fontSize: '0.6rem' }}
                        >
                            {customChangeFieldValues(item, fieldConfig)}
                        </div>
                    )}

                    {!fieldType && ( // default text field
                        <div className="subFields" style={style} title={value}>
                            <span>{value}</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };


    if (isFileFolder) {
        const nameFieldConfig = tablefieldsToShow?.find(f => f.field === 'name' && f.fieldType === 'redirectable');
        const redirecTo = nameFieldConfig?.redirecTo;
        const redirectFilterField = nameFieldConfig?.redirectFilterField;

        return (
            <div className="d-flex flex-wrap m-3" style={{ gap: '5px' }}>
                {data.map((item) => {
                    const itemId = type === 'SharedFiles'
                        ? item?.fileId?._id || item?.folderId?._id
                        : item._id;
                    if (!itemId) return null;

                    const dragDropProps = {
                        draggable: 'true',
                        onDragStart: (e) => onDragStart(e, item),
                        onDragOver,
                        onDragEnter,
                        onDragLeave,
                        onDrop: (e) => onDrop(e, item)
                    };

                    // Determine icon and color
                    const getIcon = () => {
                        if (!item.type) return 'folder';
                        switch (item.type) {
                            case 'pdf': return 'file-pdf';
                            case 'docx': return 'file-word';
                            default: return 'file';
                        }
                    };

                    const iconColor = !item.type
                        ? '#7CB9E8' // folder color
                        : item.type === 'pdf'
                            ? '#e74c3c'
                            : item.type === 'docx'
                                ? '#1292ee'
                                : '#26a8ffff';

                    // Get display name and path
                    const itemName = type === 'SharedFiles'
                        ? item?.type ? item?.fileId?.name : item?.folderId?.name
                        : item.name || item.fileName || 'Unnamed';

                    const itemPath = type === 'SharedFiles'
                        ? item?.fileId?.path || item?.folderId?.path
                        : item.path;

                    // Determine link for files (view/download)
                    const getFileLink = () => {
                        if (!item.type) return null; // it's a folder
                        if (item.type === 'docx') {
                            return `https://docs.google.com/viewer?url=${config.imgUrl}${itemPath}`;
                        }
                        return `${config.imgUrl}${itemPath}`;
                    };

                    const fileLink = getFileLink();

                    // For folders, check if redirectable
                    const isFolder = !item.type;
                    const shouldRedirectFolder = isFolder && redirecTo && redirectFilterField;

                    // Determine the link wrapper and its content
                    let linkWrapper = null;
                    let linkContent = (
                        <>
                            <div className="mt-3 mb-2">
                                <FontAwesomeIcon icon={getIcon()} style={{ color: iconColor, fontSize: '30px' }} />
                            </div>
                            <div
                                className="text-truncate w-100 small fw-bold"
                                title={itemName}
                                style={{ fontSize: '10px' }}
                            >
                                {itemName}
                            </div>
                        </>
                    );

                    if (fileLink) {
                        // File with view link
                        linkWrapper = (
                            <Link
                                to={fileLink}
                                target="_blank"
                                onClick={() => sendRecentViewedData(item)}
                                className="text-decoration-none d-flex flex-column align-items-center w-100"
                            >
                                {linkContent}
                            </Link>
                        );
                    } else if (shouldRedirectFolder) {
                        // Redirectable folder
                        const folderId = type === 'SharedFiles' ? item?.folderId?._id : item._id;
                        linkWrapper = (
                            <NavLink
                                to={`/${redirecTo}?searchId=${folderId}&searchName=${redirectFilterField}`}
                                className="text-decoration-none d-flex flex-column align-items-center w-100"
                            >
                                {linkContent}
                            </NavLink>
                        );
                    } else {
                        // Non‑clickable (e.g., folder without redirect, or noview mode)
                        linkWrapper = <div className="d-flex flex-column align-items-center w-100">{linkContent}</div>;
                    }

                    return (
                        <div key={itemId} {...dragDropProps} style={{ width: '100px' }}>
                            <Card
                                className={`${getCardClassName(item)} grid-view-card`}
                                style={{
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '8px',
                                    width: '100%',
                                    cursor: 'pointer'
                                }}
                            >
                                <CardBody className="p-2 d-flex flex-column align-items-center text-center position-relative">
                                    {/* Options menu */}
                                    <div className="position-absolute top-0 end-0" style={{ zIndex: 1 }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                showOverlay(e, item);
                                            }}
                                            aria-label="Options"
                                            role="button"
                                            className="btn btn-link p-1"
                                            style={{ fontSize: '10px' }}
                                        >
                                            <FontAwesomeIcon icon={faEllipsisV} className="text-secondary" />
                                        </button>
                                    </div>

                                    {/* Link wrapper (icon + name) */}
                                    {linkWrapper}

                                    {/* Optional metadata */}
                                    {item.size && (
                                        <div className="text-muted small" style={{ fontSize: '8px' }}>
                                            {(item.size / 1024).toFixed(1)} KB
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <Row className='m-3 global-grid-view-container hide-scrollbar' >
            {console.log(type)}
            {data.map((item) => {
                const itemId = type === 'SharedFiles'
                    ? item?.fileId?._id || item?.folderId?._id
                    : item._id;
                if (!itemId) return null;

                const dragDropProps = isFileFolder ? {
                    draggable: "true",
                    onDragStart: (e) => onDragStart(e, item),
                    onDragOver,
                    onDragEnter,
                    onDragLeave,
                    onDrop: (e) => onDrop(e, item)
                } : {};
                if (type === 'uniqueCases') {
                    return (
                        <Col xs={12} md={6} lg={4} xl={3} xxl={3} key={itemId} {...dragDropProps} style={{ padding: '2px', borderRadius: '10px' }} >
                            <Card
                                className={`${getCardClassName(item)} grid-view-card p-2 mt-2`}
                                style={{
                                    height: '100%',
                                    border: 'none',
                                    background: '#fffdfdff',
                                    color: 'white',
                                }}
                                onClick={() => openFormModal(item, 'view')}
                            >
                                <CardBody className="gridTableCardBody card-item text-align-left p-0">
                                    {/* <div className="position-relative">
                                        <div className="optionsMenu" title="Options">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showOverlay(e, item);
                                                }}
                                                aria-label="Options"
                                                role="button"
                                            >
                                                <FontAwesomeIcon
                                                    className="genderIconAlignment getcursor summarize-icon default-icon-color width-15 margin-right-7"
                                                    icon={faEllipsisV}
                                                    data-toggle="tool-tip"
                                                />
                                            </button>
                                        </div>
                                    </div> */}

                                    <Row className="m-0">
                                        {/* Left column - PDF icon and file name */}
                                        <Col xs={5} className="p-0 d-flex flex-column align-items-center justify-content-center">
                                            <FontAwesomeIcon icon="file-pdf" size="4x" style={{ color: '#d00a00ff' }} />
                                            <div
                                                className="text-center text-truncate w-100 small"
                                                title={item.fileName}
                                                style={{ fontSize: '9px', fontWeight: 'bold', cursor: 'pointer' }}
                                                onClick={() => openFormModal(item, "view")}
                                            >
                                                {item.fileName}
                                            </div>
                                        </Col>

                                        {/* Right column - Case details */}
                                        <Col xs={7} className="p-0 ps-4" style={{ fontSize: "10px", color: 'black' }}>
                                            <div className="p-2">
                                                {/* Category / Sub‑category */}
                                                <div className="mb-1">
                                                    <strong>Category:</strong>{' '}
                                                    <Badge color="info">{item.category || 'N/A'}</Badge>
                                                </div>
                                                {/* Tags (show first 2) */}
                                                {item.tags && item.tags.length > 0 && (
                                                    <div className="mb-1">
                                                        <strong>Tags:</strong>{' '}
                                                        {item.tags.slice(0, 1).map((tag, idx) => (
                                                            <Badge key={idx} color="secondary" className="me-1" style={{ fontSize: '8px' }}>
                                                                {tag}
                                                            </Badge>
                                                        ))}
                                                        {item.tags.length > 1 && <span>+{item.tags.length - 1}</span>}
                                                    </div>
                                                )}

                                                {/* Inserted date */}
                                                <div className="mb-1">
                                                    <strong>Inserted:</strong>{' '}
                                                    <span>
                                                        {item.insertedAt ? dateFormats.formatDate(item.insertedAt, config.dateDayMonthFormat) : 'N/A'}
                                                    </span>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    );
                }
                // ----- CUSTOM LAYOUT FOR USERS -----
                if (type === 'User') {
                    const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim() || 'Unnamed';
                    const address = item.address || '';

                    return (
                        <Col xs={12} md={6} lg={4} xl={3} xxl={2} key={itemId} {...dragDropProps} className="mb-3">

                            <Card
                                className={`${getCardClassName(item)} grid-view-card p-1 mx-0 my-0`}
                                style={{
                                    height: '100%',
                                    border: 'none',
                                    background: '#ffffffff',
                                    border: "1.5px solid rgb(222 222 222)"
                                }}
                                onClick={() => openFormModal(item, 'view')}
                            >

                                {/* Optionally set a smaller base font size on the CardBody */}
                                <CardBody className="tableCardBody card-item text-align-left p-1" style={{ fontSize: '10px' }}>
                                    <div className="position-relative">
                                        <div className="optionsMenu" title="Options">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showOverlay(e, item);
                                                }}
                                                aria-label="Options"
                                                role="button"
                                            >
                                                <FontAwesomeIcon
                                                    className="genderIconAlignment getcursor summarize-icon default-icon-color width-15 margin-right-7"
                                                    icon={faEllipsisV}
                                                    data-toggle="tool-tip"
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    <Row className="align-items-center">
                                        {/* Left column - Avatar */}
                                        <Col xs={3} className="text-center">
                                            {item.photo ? (
                                                <div
                                                    style={{
                                                        width: '50px',
                                                        height: '45px',
                                                        borderRadius: '50%',
                                                        backgroundColor: `hsl(${stringToHue(fullName)}, 70%, 60%)`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '20px',
                                                        fontWeight: 'bold',
                                                        color: '#fff',
                                                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                                        margin: '0 auto',
                                                    }}
                                                >
                                                    <img
                                                        src={config.profileUrl + item.photo}
                                                        alt={fullName}
                                                        className="img-fluid"
                                                        style={{ width: '50px', height: '55px', objectFit: 'cover' }}
                                                    />
                                                </div>

                                            ) : (
                                                <div
                                                    style={{
                                                        width: '45px',
                                                        height: '45px',
                                                        borderRadius: '50%',
                                                        backgroundColor: `hsl(${stringToHue(fullName)}, 70%, 60%)`,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '20px',
                                                        fontWeight: 'bold',
                                                        color: '#fff',
                                                        textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                                        margin: '0 auto',
                                                    }}
                                                >
                                                    {getInitials(item.firstName, item.lastName)}
                                                </div>
                                            )}
                                        </Col>

                                        <Col xs={9}>
                                            <div className="mb-1 d-flex align-items-center">
                                                <strong className="me-2" style={{ fontSize: '12px', maxWidth: "70%" }}>{fullName}</strong>
                                                <Badge color={getRoleBadgeColor(item.role)} pill>
                                                    {item.role}
                                                </Badge>
                                            </div>
                                            {/* Removed 'text-muted' and added custom class for dark small text */}
                                            <div className="mb-1 small text-dark-small">
                                                <span>Email: {item.email}</span>
                                            </div>
                                            <div className="mb-1 small text-dark-small">
                                                <span>Phone: {item.phone || 'No phone'}</span>
                                            </div>
                                            {address && <div className="small text-dark-small text-truncate" title={address}>
                                                Address: {address}
                                            </div>}
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    );
                }
                if (type === 'Roles') {
                    const roleName = item.role || 'Unnamed Role';
                    const roleType = item.roleType || 'N/A';
                    const status = item.status || 'Inactive';
                    const permissionsCount = item.permissions ? Object.keys(item.permissions).length : 0;

                    return (
                        <Col xs={12} md={6} lg={4} xl={3} xxl={2} key={item._id} {...dragDropProps} className="mb-3">
                            <Card
                                className={`grid-view-card h-100 p-0`}  // Add the gradient class
                                onClick={() => openFormModal(item, 'view')}
                                style={{
                                    cursor: 'pointer',
                                    border: "1.5px solid rgb(222 222 222)"

                                }}
                            >
                                <CardBody className="tableCardBody card-item text-align-left p-2" style={{ fontSize: '10px' }}>
                                    <div className="position-relative">
                                        <div className="optionsMenu" title="Options">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    showOverlay(e, item);
                                                }}
                                                aria-label="Options"
                                                role="button"
                                            >
                                                <FontAwesomeIcon
                                                    className="genderIconAlignment getcursor summarize-icon default-icon-color width-15 margin-right-7"
                                                    icon={faEllipsisV}
                                                    data-toggle="tool-tip"
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    <Row className="align-items-center">
                                        {/* Optional: Add an icon or avatar for roles */}
                                        <Col xs={3} sm={4} md={4} lg={4} xl={4} xxl={4} className="text-center">
                                            <div
                                                style={{
                                                    width: '45px',
                                                    height: '45px',
                                                    borderRadius: '50%',
                                                    backgroundColor: `hsl(${stringToHue(roleName)}, 70%, 60%)`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '1.2rem',
                                                    fontWeight: 'bold',
                                                    color: '#fff',
                                                    textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                                                    margin: '0 auto',
                                                }}
                                            >
                                                {roleName.charAt(0)}
                                            </div>

                                        </Col>

                                        {/* Right column - Role details with dark small text */}
                                        <Col xs={9} sm={8} md={8} lg={8} xl={8} xxl={8}>
                                            <div className="mb-1 d-flex align-items-center">
                                                <strong className="me-2" style={{ fontSize: '12px' }}>{roleName}</strong>
                                                <Badge color={status === 'Active' ? 'success' : 'secondary'} pill>
                                                    {status}
                                                </Badge>
                                            </div>
                                            <div className="mb-1 small text-dark-small">
                                                <span>Type: {roleType}</span>
                                            </div>
                                            <div className="mb-1 small text-dark-small">
                                                <span>Permissions: {permissionsCount} fields</span>
                                            </div>
                                            <div className="small text-dark-small text-truncate" title={`Updated: ${item.updated}`}>
                                                Updated: {new Date(item.updated).toLocaleDateString()}
                                            </div>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                    );
                }

                return (
                    <Col xs={12} sm={3} lg={2} xl={2} key={itemId} {...dragDropProps} className='my-1'>
                        <Card className={`${getCardClassName(item)} grid-view-card`}>
                            <CardBody className="iconsCardBody card-item text-align-left p-0" style={{ padding: "0 !important", fontSize: "10px" }}>
                                <div className="position-relative">
                                    <div className="optionsMenu" title="Options">
                                        <button onClick={(e) => showOverlay(e, item)} aria-label="Options" role="button">
                                            <FontAwesomeIcon
                                                className="genderIconAlignment getcursor summarize-icon default-icon-color width-15 margin-right-7"
                                                color="black"
                                                icon={faEllipsisV}
                                                data-toggle="tool-tip"
                                            />
                                        </button>
                                    </div>
                                </div>

                                {tablefieldsToShow?.map((fieldConfig) => {
                                    if (fieldConfig.showInGrid === false) return null;

                                    if (fieldConfig.showIcon) {
                                        return (
                                            <div key={fieldConfig.field} className={fieldConfig.customWidth || 'col-12'}>
                                                {/* {renderFileFolderIcon(fieldConfig, item)} */}
                                            </div>
                                        );
                                    }

                                    return renderField(fieldConfig, item);
                                })}
                            </CardBody>
                        </Card>
                    </Col>
                );
            })}
        </Row >
    );
};

export default GridView;